import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/Button';
import { 
  Mail, 
  MessageSquare, 
  AlertCircle, 
  Briefcase, 
  PenTool, 
  HelpCircle, 
  CheckCircle, 
  Send
} from 'lucide-react';

type ContactType = 'general' | 'support' | 'author' | 'partner' | 'complaint' | 'feedback';

export const Contact: React.FC = () => {
  const { surfaceClass, bgClass } = useTheme();
  const [activeType, setActiveType] = useState<ContactType>('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    url: '', // For complaints/authors
    metaInfo: '' // For device info or company name
  });

  const contactOptions: { id: ContactType; label: string; icon: React.ElementType; desc: string }[] = [
    { id: 'general', label: 'General Inquiry', icon: Mail, desc: 'General questions about Readsy.' },
    { id: 'support', label: 'Tech Support', icon: HelpCircle, desc: 'Login issues, bugs, or app problems.' },
    { id: 'author', label: 'For Authors', icon: PenTool, desc: 'Publishing inquiries and author tools.' },
    { id: 'partner', label: 'Partnership', icon: Briefcase, desc: 'Business and collaboration proposals.' },
    { id: 'feedback', label: 'Feedback', icon: MessageSquare, desc: 'Suggestions and feature requests.' },
    { id: 'complaint', label: 'Report / Legal', icon: AlertCircle, desc: 'Copyright claims or content reporting.' },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log(`Submitting [${activeType}] form:`, formData);
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center p-6 text-center animate-in zoom-in duration-300">
        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6">
          <CheckCircle size={40} />
        </div>
        <h1 className="font-serif text-3xl font-bold mb-4">Message Sent!</h1>
        <p className="opacity-70 max-w-md mb-8 leading-relaxed">
          Thank you for reaching out. We've received your {activeType} request and will get back to you at <strong>{formData.email}</strong> as soon as possible.
        </p>
        <Button onClick={() => window.location.reload()} variant="outline">
          Send Another Message
        </Button>
      </div>
    );
  }

  return (
    <div className="pb-24 px-6 pt-8 animate-in slide-in-from-bottom-4 duration-500 max-w-3xl mx-auto">
      
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="font-serif text-3xl md:text-4xl font-bold mb-4">How can we help?</h1>
        <p className="opacity-60 max-w-lg mx-auto">
          We'd love to hear from you. Please select a topic below so we can route your message to the right team.
        </p>
      </div>

      {/* Category Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-10">
        {contactOptions.map((opt) => (
          <button
            key={opt.id}
            onClick={() => setActiveType(opt.id)}
            className={`p-4 rounded-xl border text-left transition-all duration-200 flex flex-col items-start gap-3 ${
              activeType === opt.id 
                ? 'border-brand-blue bg-brand-blue/5 shadow-md ring-1 ring-brand-blue' 
                : `border-black/5 dark:border-white/5 ${surfaceClass} hover:border-black/20 dark:hover:border-white/20`
            }`}
          >
            <div className={`p-2 rounded-lg ${activeType === opt.id ? 'bg-brand-blue text-white' : 'bg-black/5 dark:bg-white/10 opacity-70'}`}>
              <opt.icon size={20} />
            </div>
            <div>
              <div className={`font-bold text-sm mb-1 ${activeType === opt.id ? 'text-brand-blue' : ''}`}>{opt.label}</div>
              <div className="text-[10px] opacity-50 leading-tight">{opt.desc}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Dynamic Form */}
      <div className={`p-6 md:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm ${surfaceClass}`}>
        <h2 className="font-bold text-xl mb-6 flex items-center gap-2">
          {contactOptions.find(o => o.id === activeType)?.icon && React.createElement(contactOptions.find(o => o.id === activeType)!.icon, { size: 24, className: "text-brand-blue" })}
          {contactOptions.find(o => o.id === activeType)?.label} Form
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Your Name</label>
              <input 
                type="text" 
                name="name"
                required
                className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-blue transition-all"
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Email Address</label>
              <input 
                type="email" 
                name="email"
                required
                className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-blue transition-all"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Dynamic Fields based on Type */}
          
          {/* Author: Portfolio Link */}
          {activeType === 'author' && (
             <div>
              <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Portfolio / Previous Works URL</label>
              <input 
                type="url" 
                name="url"
                placeholder="https://..."
                className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-blue transition-all"
                value={formData.url}
                onChange={handleInputChange}
              />
            </div>
          )}

          {/* Support: Device Info */}
          {activeType === 'support' && (
             <div>
              <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Device & Browser (Optional)</label>
              <input 
                type="text" 
                name="metaInfo"
                placeholder="e.g. iPhone 13, Safari"
                className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-blue transition-all"
                value={formData.metaInfo}
                onChange={handleInputChange}
              />
            </div>
          )}

          {/* Partner: Company Name */}
          {activeType === 'partner' && (
             <div>
              <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Company / Organization</label>
              <input 
                type="text" 
                name="metaInfo"
                className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-blue transition-all"
                value={formData.metaInfo}
                onChange={handleInputChange}
              />
            </div>
          )}

          {/* Complaint: Content URL */}
          {activeType === 'complaint' && (
             <div>
              <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Content URL (Book/Chapter link)</label>
              <input 
                type="url" 
                name="url"
                required
                placeholder="https://readsy.link/book/..."
                className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-blue transition-all"
                value={formData.url}
                onChange={handleInputChange}
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Subject</label>
            <input 
              type="text" 
              name="subject"
              required
              className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-blue transition-all"
              value={formData.subject}
              onChange={handleInputChange}
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider opacity-50 mb-1.5">Message</label>
            <textarea 
              name="message"
              required
              rows={5}
              placeholder={activeType === 'feedback' ? "Tell us what you love or what we could improve..." : "How can we help?"}
              className="w-full bg-black/5 dark:bg-white/5 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-brand-blue transition-all resize-none"
              value={formData.message}
              onChange={handleInputChange}
            />
          </div>

          <Button type="submit" size="lg" fullWidth disabled={isSubmitting} className="shadow-xl shadow-brand-blue/20">
            {isSubmitting ? 'Sending...' : 'Send Message'}
            {!isSubmitting && <Send size={18} className="ml-2" />}
          </Button>

          <p className="text-center text-xs opacity-40">
            By submitting this form, you agree to our privacy policy. <br/>
            IP address recorded for security.
          </p>
        </form>
      </div>

      {/* Alternative Info */}
      <div className="mt-12 grid md:grid-cols-2 gap-6 text-center md:text-left">
        <div className={`p-6 rounded-xl ${bgClass} border border-black/5 dark:border-white/5`}>
            <h3 className="font-bold mb-2">Email Us Directly</h3>
            <p className="opacity-60 text-sm mb-1">For urgent matters:</p>
            <a href="mailto:hello@readsy.link" className="text-brand-blue font-medium hover:underline">hello@readsy.link</a>
        </div>
        <div className={`p-6 rounded-xl ${bgClass} border border-black/5 dark:border-white/5`}>
            <h3 className="font-bold mb-2">Socials</h3>
            <p className="opacity-60 text-sm mb-1">Follow our updates:</p>
            <div className="space-x-4">
                <a href="#" className="text-brand-blue font-medium hover:underline">Twitter</a>
                <a href="#" className="text-brand-blue font-medium hover:underline">Instagram</a>
            </div>
        </div>
      </div>
    </div>
  );
};